<template>
  <div class="client-list-page">
    <div class="-content d-flex align-items-start flex-column">
      <div class="page-global-header px-0 mt-3 w-100">
        <div class="row justify-content-center mx-0">
          <div class="col-sm-12 col-md-12 col-lg-12 col-xl-6 col-xxl-6 offset-xl-3 offset-xxl-3">
            <div class="global-searchbar">
              <form class="base-form" @submit.prevent="">
                <div class="input-group">
                  <div class="input-group-text">
                    <img src="@/assets/images/system/groups.svg" style="width: 20px" />
                  </div>
                  <input type="search" v-model="searchQuery" class="form-control"
                    placeholder="(Company name, Client name, Email etc)" />
                  <button type="button" class="btn btn-primary">
                    <i class="fa fa-search"></i>
                  </button>
                </div>
              </form>
            </div>
          </div>
          <div class="col-sm-12 col-md-12 col-lg-12 col-xl-3 col-xxl-3 d-flex align-items-center justify-content-end"
            style="padding-right: 200px;">
            <ul class="search-option-menu me-3">
              <li class="nav-item sorting">
                <input type="checkbox" class="sorting-check" id="btn-sorting-page" autocomplete="off"
                  @change="toggleSortOrder" :checked="client_sorting_desc" />
                <label class="btn btn-sorting" for="btn-sorting-page"><span class="sr-only">Sort</span></label>
              </li>
              <li class="nav-item ms-2">
                <router-link :to="{ name: 'add-client' }" class="nav-link circleNav">
                  <i class="fa fa-plus"></i>
                </router-link>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="cl-addressbook">
        <div class="cl-ab-panel">
          <!-- ── Scrollable client list ── -->
          <div class="cl-ab-list">
            <div v-if="!filteredClients.length" class="cl-empty">
              No clients found. Try a different search.
            </div>

            <template v-else>
              <div class="cl-contact-card" v-for="(client, index) in paginatedClients" :key="client.id || index"
                role="button" tabindex="0" title="View client details" @click="showViewData(client.id)"
                @keydown.enter="showViewData(client.id)">
                <!-- Avatar -->
                <div class="cl-contact-avatar">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10Z" />
                    <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
                  </svg>
                </div>

                <!-- Body -->
                <div class="cl-contact-body">
                  <!-- Kebab menu -->
                  <div class="cl-kebab-wrap" @click.stop>
                    <button class="cl-kebab" type="button" title="More options" aria-label="More options"
                      @click="toggleKebab(client.id || index)">
                      <span></span><span></span><span></span>
                    </button>
                    <div class="cl-kebab-menu" :class="{ open: openKebabId === (client.id || index) }">
                      <button type="button" @click="showEditData(client.id)">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                          stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                        Edit
                      </button>
                      <button type="button" class="delete" @click="deleteClient(client.id)">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                          stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                          <path d="M10 11v6" />
                          <path d="M14 11v6" />
                          <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                        </svg>
                        Delete
                      </button>
                    </div>
                  </div>

                  <div v-if="client.full_name" class="cl-contact-line company">
                    <span class="num">{{ (currentPage - 1) * pageSize + index + 1 }}.</span>
                    <span class="tag">N:</span> {{ client.full_name }}
                  </div>
                  <div class="cl-contact-line" :class="{ company: !client.full_name }">
                    <span v-if="!client.full_name" class="num">{{ (currentPage - 1) * pageSize + index + 1 }}.</span>
                    <span class="tag muted-tag">Co:</span> {{ client.business_name }}
                  </div>
                  <div v-if="client.phone1" class="cl-contact-line">
                    <span class="tag muted-tag">T:</span> {{ client.phone1 }}
                  </div>
                  <div v-if="client.email" class="cl-contact-line email">
                    <span class="tag">E:</span>
                    <a :href="'mailto:' + client.email" @click.stop>{{ client.email }}</a>
                  </div>

                  <!-- Notes badge -->
                  <div class="cl-card-actions" @click.stop v-if="client.notes && client.notes !== 'N/A'">
                    <button type="button" class="note-btn ms-2" title="Client Note" aria-label="Client Note"
                      data-bs-toggle="modal" data-bs-target="#expenseNote" @click="fileExpensesDetails(client.notes)">
                      <i class="far fa-file-alt"></i> Note
                    </button>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <!-- ── Footer: pagination ── -->
          <div class="cl-ab-footer" v-if="filteredClients.length">
            <el-pagination background layout="prev, pager, next" :pager-count="5" :total="filteredClients.length"
              :page-size="pageSize" :current-page="currentPage" @current-change="handlePageChange" />
          </div>
        </div>
      </div>
    </div>

    <!-- Notes modal -->
    <div class="modal fade mdl-messaging _info" id="expenseNote" aria-hidden="true" aria-labelledby="expenseNoteLabel"
      tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-dialog-header">
          <div class="header-title">Client Note</div>
        </div>
        <div class="modal-content">
          <div class="modal-header">
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close">
              <i class="fa fa-times"></i>
            </button>
          </div>
          <div class="modal-body pt-0">
            <div class="message-card no-shadow m-0">
              <div class="card card--curve-05rem mt-3 w-100">
                <div class="card-body">
                  <p class="m-0">Note:</p>
                  <p class="m-0">{{ note }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ListClient",
  data() {
    return {
      client_server_data: [],
      note: "",
      client_sorting_desc: false,
      searchQuery: "",
      openKebabId: null,
      currentPage: 1,
      pageSize: 6,
    };
  },
  computed: {
    filteredClients() {
      let list = this.client_server_data;
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        list = list.filter((client) => {
          return (
            (client.business_name &&
              client.business_name.toLowerCase().includes(query)) ||
            (client.full_name && client.full_name.toLowerCase().includes(query)) ||
            (client.email && client.email.toLowerCase().includes(query)) ||
            (client.phone1 && client.phone1.toLowerCase().includes(query))
          );
        });
      }
      const sorted = [...list];
      sorted.sort((a, b) => {
        const cmp = String(a.business_name ?? "").localeCompare(
          String(b.business_name ?? "")
        );
        return this.client_sorting_desc ? -cmp : cmp;
      });
      return sorted;
    },
    paginatedClients() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredClients.slice(start, start + this.pageSize);
    },
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
  },
  created() {
    this.runMountedFunctions();
  },
  mounted() {
    // Close kebab menus on outside click
    document.addEventListener("click", this.closeAllKebabs);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.closeAllKebabs);
  },
  methods: {
    showLoading() {
      this.loadingInstance = this.$loading({
        lock: true,
        text: "Loading",
        background: "rgba(0, 0, 0, 0.7)",
        fullscreen: true,
      });
    },
    hideLoading() {
      if (this.loadingInstance) {
        this.loadingInstance.close();
      }
    },
    runMountedFunctions() {
      this.getData();
    },
    getData() {
      this.showLoading();
      this.$store
        .dispatch("client/getListByUser", {})
        .then((res) => {
          // Unwrap only a raw axios response. The API envelope itself has a top-level
          // `data` key, so a plain truthiness check on res.data grabbed the payload
          // array instead of the envelope and every list rendered empty.
          const apiRes = res?.data?.status !== undefined ? res.data : res;
          if (apiRes.status == "1") {
            this.client_server_data = apiRes.response;
          } else {
            const errorMsg =
              apiRes.message?.length > 0
                ? Array.isArray(apiRes.message)
                  ? apiRes.message.join("\n")
                  : apiRes.message
                : "Something went wrong. Please try again.";
            this.$toast.error(errorMsg);
            throw new Error(errorMsg);
          }
        })
        .catch((err) => {
          console.log(err);
          this.client_server_data = [];
          if (!err.message) {
            this.$toast.error("Something went wrong. Please try again.");
          }
        })
        .finally(() => {
          this.hideLoading();
        });
    },
    deleteClient(client_id) {
      if (!confirm("Delete this client? This cannot be undone.")) return;
      this.showLoading();
      this.$store
        .dispatch("client/deleteClient", { client_id: client_id })
        .then((res) => {
          if (res.status == 1) {
            this.$toast.success("Delete data Successful.");
            this.getData();
          } else {
            throw new Error(res);
          }
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          this.hideLoading();
        });
    },
    fileExpensesDetails(note) {
      this.note = note;
    },
    toggleSortOrder() {
      this.client_sorting_desc = !this.client_sorting_desc;
    },
    showEditData(id) {
      this.$router.push({ name: "edit-client", params: { id: id } });
    },
    showViewData(id) {
      if (!id) return;
      this.$router.push({ name: "view-client", params: { id: id } });
    },
    toggleKebab(id) {
      this.openKebabId = this.openKebabId === id ? null : id;
    },
    closeAllKebabs() {
      this.openKebabId = null;
    },
    handlePageChange(newPage) {
      this.currentPage = newPage;
    },
  },
};
</script>

<style scoped>
/* ── Design tokens (scoped to this component only) ── */
.cl-addressbook {
  --teal: #18798c;
  --teal-dark: #136374;
  --red: #e03131;
  --green: #2da44e;
  --amber-person: #f2b705;
  --card: #ffffff;
  --ink: #2c3038;
  --muted: #8a909a;
  --line: #e6e8eb;

  width: 100%;
  display: flex;
  justify-content: center;
  padding: 16px;
}

.cl-ab-panel {
  width: 100%;
  max-width: 45%;
  padding: 10px 0;
  display: flex;
  flex-direction: column;
  min-height: 600px;
}

/* ── Client list ── */
.cl-ab-list {
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0;
  flex: 1;
  min-height: 400px;
  overflow-y: auto;
}

.cl-empty {
  text-align: center;
  color: var(--muted);
  padding: 48px 16px;
  font-size: 14px;
}

/* ── Contact card ── */
.cl-contact-card {
  height: 113px;
  flex-shrink: 0;
  display: flex;
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(20, 30, 50, 0.05);
  overflow: hidden;
  cursor: pointer;
  transition: box-shadow 0.15s, border-color 0.15s;
}

.cl-contact-card:hover,
.cl-contact-card:focus-visible {
  border-color: var(--teal);
  box-shadow: 0 3px 10px rgba(20, 30, 50, 0.12);
  outline: none;
}

.cl-contact-avatar {
  flex-shrink: 0;
  width: 56px;
  background: var(--teal);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--amber-person);
}

.cl-contact-body {
  flex: 1;
  padding: 12px 14px;
  position: relative;
  min-width: 0;
  padding-right: 36px;
  /* room for kebab */
}

.cl-contact-line {
  font-size: 14px;
  line-height: 1.55;
  word-break: break-word;
  color: var(--ink);
}

.cl-contact-line .tag {
  font-weight: 700;
}

.cl-contact-line.company {
  font-weight: 600;
}

.cl-contact-line.company .num {
  color: var(--red);
  font-weight: 700;
}

.cl-contact-line.email {
  color: var(--red);
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.cl-contact-line.email a {
  color: var(--red);
  text-decoration: none;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cl-contact-line.email a:hover {
  text-decoration: underline;
}

.muted-tag {
  color: var(--muted);
}

/* ── Kebab menu ── */
.cl-kebab-wrap {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
}

.cl-kebab {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 24px;
  height: 24px;
}

.cl-kebab:hover {
  background: rgba(0, 0, 0, 0.06);
}

.cl-kebab span {
  display: block;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #6b7280;
  flex-shrink: 0;
}

.cl-kebab:hover span {
  background: #374151;
}

.cl-kebab-menu {
  display: none;
  position: absolute;
  top: -4px;
  right: 0;
  min-width: 140px;
  background: rgba(255, 255, 255, 0.98);
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.14), 0 1px 4px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  z-index: 200;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.cl-kebab-menu.open {
  display: block;
}

.cl-kebab-menu button {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 500;
  color: var(--ink);
  text-align: left;
  font-family: inherit;
  transition: background 0.12s ease;
}

.cl-kebab-menu button:not(:last-child) {
  border-bottom: 1px solid rgba(0, 0, 0, 0.07);
}

.cl-kebab-menu button:hover {
  background: rgba(0, 0, 0, 0.05);
}

.cl-kebab-menu button.delete {
  color: var(--red);
}

/* ── Notes badge ── */
.cl-card-actions {
  position: absolute;
  right: 10px;
  bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.cl-contact-card .note-btn {
  background-color: var(--teal);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 5px 12px;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: background-color 0.15s;
}

.cl-contact-card .note-btn:hover {
  background-color: var(--teal-dark);
  color: #fff;
}

.cl-contact-card .note-btn i {
  font-size: 13px;
}

/* Scrollbar */
.cl-ab-list::-webkit-scrollbar {
  width: 5px;
}

.cl-ab-list::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.cl-ab-list::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 4px;
}

/* ── Footer: pagination ── */
.cl-ab-footer {
  display: flex;
  justify-content: center;
  padding: 16px 0 4px;
}

/* ── Responsive ── */
@media (max-width: 992px) {
  .cl-ab-panel {
    max-width: 90%;
  }
}

@media (max-width: 540px) {
  .cl-ab-panel {
    max-width: 100%;
    padding: 20px 16px 18px;
  }

  .cl-ab-list {
    padding: 0;
  }

  .cl-contact-avatar {
    width: 46px;
  }
}
</style>
